import { TaxStrategy } from '@principle-theorem/accounting';
import {
  ChartableSurface,
  ChartedItemType,
  PricingRuleType,
  PricingScope,
  PricingUnitType,
  ScopedSurface,
  ServiceCode,
  ServiceCodeGroupType,
  ServiceCodeScope,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  IAvroSerialisedVersionedSchema,
  ISerialisedDocumentReference,
  ISerialisedTimestamp,
} from '@principle-theorem/shared';
import {
  IAppointmentDimension,
  IBigQueryAssociatedTreatmentCategory,
} from '../dimensions/appointment';
import { IBrandDimension } from '../dimensions/brand';
import {
  IBigQueryDepositLineItem,
  IBigQueryEvent,
  IBigQueryNamedDocument,
  IBigQueryTreatmentLineItem,
} from '../dimensions/common';
import { IInvoiceDimension } from '../dimensions/invoice';
import { ILabDimension } from '../dimensions/lab';
import { ILabJobDimension } from '../dimensions/lab-job';
import { IBigQueryReferrer, IPatientDimension } from '../dimensions/patient';
import { IPaymentPlanDimension } from '../dimensions/payment-plan';
import { IPracticeDimension } from '../dimensions/practice';
import { IRecurringTaskDimension } from '../dimensions/recurring-task';
import { IStafferDimension } from '../dimensions/staffer';
import { ITaskDimension } from '../dimensions/task';
import { ITeamDimension } from '../dimensions/team';
import { ITransactionDimension } from '../dimensions/transaction';
import { ITreatmentPlanDimension } from '../dimensions/treatment-plan';

export interface ITimestampedFact {
  timestamp: ISerialisedTimestamp;
}

export interface IBaseEventFact<Action, Status> extends ITimestampedFact {
  event: IModelEvent<Action, Status>;
}

export interface IModelEvent<Action, Status> {
  action?: Action;
  statusBefore: Status;
  statusAfter: Status;
}

export interface IBrandScopedFact {
  brandRef: ISerialisedDocumentReference;
  brand: IBrandDimension;
}

export interface IPatientScopedFact extends IBrandScopedFact {
  patientRef: ISerialisedDocumentReference;
  patient: IPatientDimension;
  referrer?: IBigQueryReferrer;
}

export interface IAppointmentScopedFact extends IPatientScopedFact {
  appointmentRef: ISerialisedDocumentReference;
  practitionerRef: ISerialisedDocumentReference;
  treatmentPlanRef: ISerialisedDocumentReference;
  practiceRef: ISerialisedDocumentReference;
  appointment: IAppointmentDimension;
  practitioner: IStafferDimension;
  treatmentPlan: ITreatmentPlanDimension;
  practice: IPracticeDimension;
}

export interface IInvoiceScopedFact extends IPatientScopedFact {
  invoiceRef: ISerialisedDocumentReference;
  practiceRef: ISerialisedDocumentReference;
  transactionRefs: ISerialisedDocumentReference[];
  invoice: IInvoiceDimension;
  practice: IPracticeDimension;
  transactions: ITransactionDimension[];
}

export interface ITreatmentStepScopedFact
  extends Omit<
    ITreatmentPlanScopedFact,
    'treatmentPlan' | 'patient' | 'referrer'
  > {
  treatmentStepRef: ISerialisedDocumentReference;
  appointmentRef?: ISerialisedDocumentReference;
  practiceRef?: ISerialisedDocumentReference;
  invoiceRef?: ISerialisedDocumentReference;
}

export interface ITreatmentPlanScopedFact extends IPatientScopedFact {
  practitionerRef?: ISerialisedDocumentReference;
  practitioner?: IStafferDimension;
  treatmentPlanRef: ISerialisedDocumentReference;
  treatmentPlan: ITreatmentPlanDimension;
}

export interface IPracticeScopedFact extends IBrandScopedFact {
  practiceRef: ISerialisedDocumentReference;
  practice: IPracticeDimension;
}

export interface IGapScopedFact extends IPracticeScopedFact {
  gap: IBigQueryGap;
  practitionerRef: ISerialisedDocumentReference;
  practitioner: IStafferDimension;
}

export interface IBigQueryGap {
  event: IBigQueryEvent;
  practitionerRef: ISerialisedDocumentReference;
  practitioner: IBigQueryNamedDocument;
}

export interface ITaskScopedFact extends IPracticeScopedFact {
  taskRef: ISerialisedDocumentReference;
  ownerRef?: ISerialisedDocumentReference;
  assignedUserRef?: ISerialisedDocumentReference;
  assignedTeamRef?: ISerialisedDocumentReference;
  task: ITaskDimension;
  recurringConfiguration?: IRecurringTaskDimension;
  owner?: IStafferDimension;
  assignedUser?: IStafferDimension;
  assignedTeam?: ITeamDimension;
}

export interface ILabJobScopedFact extends IPracticeScopedFact {
  labRef: ISerialisedDocumentReference;
  labJobRef: ISerialisedDocumentReference;
  patientRef?: ISerialisedDocumentReference;
  appointmentRef?: ISerialisedDocumentReference;
  treatmentPlanRef?: ISerialisedDocumentReference;
  practitionerRef?: ISerialisedDocumentReference;
  lab: ILabDimension;
  labJob: ILabJobDimension;
  patient?: IPatientDimension;
  referrer?: IBigQueryReferrer;
  appointment?: IAppointmentDimension;
  treatmentPlan?: ITreatmentPlanDimension;
  practitioner?: IStafferDimension;
}

export interface IPaymentPlanScopedFact extends IPatientScopedFact {
  paymentPlan: IPaymentPlanDimension;
}

interface IBigQueryChartedItem {
  uuid: string;
  type: ChartedItemType;
  config: IBigQueryNamedDocument;
  chartedSurfaces: IBigQueryChartedSurface[];
  resolvedBy?: IBigQueryNamedDocument;
  resolvedAt?: ISerialisedTimestamp;
  notes: IBigQueryClinicalNote[];
  scopeRef: IBigQueryScopeRef;
}

export interface IBigQueryChartedTreatment extends IBigQueryChartedItem {
  type: ChartedItemType;
  serviceCodeSmartGroups: IBigQueryChartedServiceSmartGroup[];
  serviceCodeGroups: IBigQueryServiceCodeGroup<IBigQueryPricedServiceCodeEntry>[];
  serviceCodes: IBigQueryPricedServiceCodeEntry[];
  invoices: IBigQueryInvoiceReference[];
  price: number;
  basePrice: number;
  feeSchedule: IBigQueryNamedDocument;
  attributedTo?: IBigQueryNamedDocument;
}

interface IBigQueryServiceCodeGroup<
  T extends IBigQueryServiceCodeEntry = IBigQueryServiceCodeEntry,
> {
  type: ServiceCodeGroupType;
  serviceCodes: T[];
}

interface IBigQueryInvoiceReference {
  invoiceRef: ISerialisedDocumentReference;
  lineItemUid: string;
  amount: number;
}

export interface IBigQueryScopeRef {
  scope: ScopedSurface;
  ref?: number | IBigQueryToothRef;
}

interface IBigQueryClinicalNote {
  uid: string;
  content: IAvroSerialisedVersionedSchema;
  immutable: boolean;
  owner: IBigQueryNamedDocument;
  createdAt: ISerialisedTimestamp;
  updatedAt: ISerialisedTimestamp;
}

interface IBigQueryChartedServiceSmartGroup
  extends IBigQueryServiceSmartGroup<IBigQueryPricedServiceCodeEntry> {
  selected?: ServiceCode;
}

interface IBigQueryServiceSmartGroup<T = ServiceCode> {
  uid: string;
  title: string;
  description: string;
  scope: ServiceCodeScope;
  serviceCodes: T[];
}

interface IBigQueryServiceCodeEntry {
  type: ServiceCodeType;
  code: ServiceCode;
  quantity: number;
}

export interface IBigQueryPricedServiceCodeEntry
  extends IBigQueryServiceCodeEntry {
  pricingRule: IBigQueryPricingRule;
  price: number;
  priceOverride?: number;
  tax: number;
  taxStatus: TaxStrategy;
  chartedItemOrigin?: IBigQueryNamedDocument;
  chartedSurfaces: IBigQueryChartedSurface[];
}

export interface ISerialisedPricedServiceCodeSummary
  extends IBigQueryServiceCodeEntry {
  code: string;
  price: number;
  tax: number;
  chartedSurfaces: string[];
  practitioner?: IBigQueryNamedDocument;
  treatment: IBigQueryNamedDocument;
  treatmentCategory?: IBigQueryAssociatedTreatmentCategory;
}

interface IBigQueryPricingRule {
  type: PricingRuleType;
  ruleItems: IBigQueryPricingRuleItem[];
  unitType?: PricingUnitType;
  scope?: PricingScope;
}

interface IBigQueryPricingRuleItem {
  uid: string;
  price?: number;
  startUnit: number;
  endUnit?: number;
  selected: boolean;
}

interface IBigQueryChartedSurface {
  uuid: string;
  chartedBy: IBigQueryNamedDocument;
  chartedAt: ISerialisedTimestamp;
  resolvedBy?: IBigQueryNamedDocument;
  resolvedAt?: ISerialisedTimestamp;
  chartedRef: Partial<IBigQueryChartedRef>;
}

interface IBigQueryChartedRef {
  wholeMouth: boolean;
  /**
   * Arch is an invalid avro enum
   */
  arch: number;
  /**
   * Quadrant is an invalid avro enum
   */
  quadrant: number;
  multipleTeeth: IBigQueryToothRef[];
  tooth: IBigQueryChartedTooth;
  unscoped: boolean;
}

interface IBigQueryToothRef {
  /**
   * Quadrant is an invalid avro enum
   */
  quadrant: number;
  quadrantIndex: number;
}

interface IBigQueryChartedTooth extends IBigQueryToothRef {
  surface?: ChartableSurface;
}

export interface IBigQueryInvoicePracitionerSummary {
  practitioner: IBigQueryNamedAllocationTarget;
  allocatedPercent: number;
  treatments: IBigQueryTreatmentLineItem[];
  treatmentsTotal: number;
  deposits: IBigQueryDepositLineItem[];
  depositsTotal: number;
  practitionerTotal: number;
}

export interface IBigQueryPaymentPracitionerSummary {
  practitioner: IBigQueryNamedAllocationTarget;
  allocatedPercent: number;
  allocatedAmount: number;
}

export type IBigQueryTransactionAllocationTarget =
  | ISerialisedDocumentReference
  | undefined;

export interface IBigQueryNamedAllocationTarget {
  name: string;
  ref: IBigQueryTransactionAllocationTarget;
}
